"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import Script from "next/script";

// @types/navermaps 설치 후 타입 사용

interface NaverMapSimpleProps {
  address: string;
  name: string;
}

// 1. 전역 변수로 스크립트 로딩 상태 추적
let isNaverMapScriptLoaded = false;

const NaverMapSimple = ({ address, name }: NaverMapSimpleProps) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<naver.maps.Map | null>(null);
  // 2. 두 가지 상태 추적: 스크립트 로딩 및 지도 초기화
  const [isScriptLoaded, setIsScriptLoaded] = useState(isNaverMapScriptLoaded);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 3. 스크립트 로드 완료 핸들러 개선
  const handleScriptLoad = useCallback(() => {
    console.log("Naver Maps script loaded");
    isNaverMapScriptLoaded = true;
    setIsScriptLoaded(true);
    // 스크립트가 로드되었을 때 initializeMap을 직접 호출하지 않음
    // useEffect에서 처리
  }, []);

  // 4. 지도 초기화 함수 개선
  const initializeMap = useCallback(() => {
    // 5. 스크립트 로딩 및 DOM 요소 확인
    if (!mapRef.current) {
      console.error("Map container not found");
      return;
    }

    // 6. window.naver 객체 확인
    if (typeof window === "undefined" || !window.naver || !window.naver.maps) {
      console.error("Naver maps not available");
      return;
    }

    // 7. 이미 초기화된 경우 중복 방지
    if (mapInstanceRef.current) {
      console.log("Map already initialized");
      setIsMapInitialized(true);
      setIsLoading(false);
      return;
    }

    try {
      console.log("Initializing map with address:", address);

      // 8. 주소 검색 및 지도 생성
      window.naver.maps.Service.geocode(
        { query: address },
        (status, response) => {
          if (
            status !== window.naver.maps.Service.Status.OK ||
            !response.v2.addresses.length
          ) {
            console.error("Geocoding failed:", status);
            const defaultCenter = new window.naver.maps.LatLng(
              37.5665,
              126.978
            );
            const mapOptions = {
              center: defaultCenter,
              zoom: 17,
              mapDataControl: false,
            };
            mapInstanceRef.current = new window.naver.maps.Map(
              mapRef.current!,
              mapOptions
            );
            new window.naver.maps.Marker({
              position: defaultCenter,
              map: mapInstanceRef.current,
              title: name,
            });

            setIsMapInitialized(true);
            setIsLoading(false);
            return;
          }

          const result = response.v2.addresses[0];
          const point = new window.naver.maps.LatLng(
            Number(result.y),
            Number(result.x)
          );

          const mapOptions = {
            center: point,
            zoom: 17,
            mapDataControl: false,
          };

          mapInstanceRef.current = new window.naver.maps.Map(
            mapRef.current!,
            mapOptions
          );

          new window.naver.maps.Marker({
            position: point,
            map: mapInstanceRef.current,
            title: name,
          });

          console.log("Naver Map initialized successfully for:", address);
          setIsMapInitialized(true);
          setIsLoading(false);

          // 컨테이너 크기가 확정된 다음 프레임에 뷰포트를 갱신하고
          // 마커 좌표로 재센터링 (크기 0 상태에서 초기화되면 초기
          // 화면이 마커에서 밀려 표시되는 문제 방지)
          requestAnimationFrame(() => {
            if (mapInstanceRef.current) {
              window.naver.maps.Event.trigger(
                mapInstanceRef.current,
                "resize"
              );
              mapInstanceRef.current.setCenter(point);
            }
          });
        }
      );
    } catch (error) {
      console.error("Error initializing map:", error);
      setIsLoading(false);
    }
  }, [address, name]);

  // 9. 지도 초기화를 위한 useEffect 분리 및 개선
  useEffect(() => {
    // 스크립트가 로드되었을 때만 지도 초기화
    if (isScriptLoaded && !isMapInitialized) {
      console.log("Script is loaded, initializing map");
      // 약간의 지연을 주어 DOM과 naver 객체 준비 보장
      const timer = setTimeout(() => {
        initializeMap();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isScriptLoaded, isMapInitialized, initializeMap]);

  // 10. 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
        setIsMapInitialized(false);
        console.log("Naver Map instance destroyed.");
      }
    };
  }, []);

  return (
    <>
      {/* 11. 스크립트가 이미 로드되지 않았을 때만 스크립트 태그 추가 */}
      {!isNaverMapScriptLoaded && (
        <Script
          strategy="afterInteractive"
          src={`https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${process.env.NEXT_PUBLIC_NAVER_CLIENT_ID}&submodules=geocoder`}
          onReady={handleScriptLoad}
          onError={(e) => {
            console.error("Naver Map Script load error:", e);
            setIsLoading(false);
          }}
        />
      )}

      {/* 12~13. 지도 컨테이너 — 항상 실제 크기를 유지해야 지도 초기
          중심이 올바르게 계산됨. 로딩 표시는 오버레이로 얹음 */}
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <div
          ref={mapRef}
          style={{ width: "100%", height: "100%" }}
          data-naver-map
        />
        {isLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#e0e0e0",
            }}
          >
            지도 로딩 중...
          </div>
        )}
      </div>
    </>
  );
};

// declare global 제거
// interface Window {
//   naver: any;
// }

export default NaverMapSimple;
